import {
  CloudDownload as DownloadIcon,
  NoteAdd as NoteAddIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import type { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDownloadCallRecording } from "../../customers/callRecordings/useDownloadCallRecording";

export interface AgentPhoneCallRow {
  readonly id: string;
  readonly clientName: string;
  readonly clientId: string | null;
  readonly agentName: string;
  readonly date: string;
  readonly startTime: string;
  readonly duration: string;
  readonly isPlayable: boolean;
}

interface Props {
  readonly rows: AgentPhoneCallRow[];
  readonly playingRecId: string | null;
  readonly loadingRecId: string | null;
  readonly generatingNoteRecId: string | null;
  readonly onPlayToggle: (id: string) => void;
  readonly onCreateNoteDraft: (id: string) => void;
}

export const AgentCallsList: FC<Props> = ({
  rows,
  playingRecId,
  loadingRecId,
  generatingNoteRecId,
  onPlayToggle,
  onCreateNoteDraft,
}) => (
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Data</TableCell>
          <TableCell>Klient</TableCell>
          <TableCell>Pracownik</TableCell>
          <TableCell align="right">Czas trwania</TableCell>
          <TableCell align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <AgentPhoneCallRowItem
            key={row.id}
            row={row}
            isPlaying={playingRecId === row.id}
            isLoading={loadingRecId === row.id}
            isCreatingNote={generatingNoteRecId === row.id}
            onPlayToggle={onPlayToggle}
            onCreateNoteDraft={onCreateNoteDraft}
          />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

interface RowProps {
  readonly row: AgentPhoneCallRow;
  readonly isPlaying: boolean;
  readonly isLoading: boolean;
  readonly isCreatingNote: boolean;
  readonly onPlayToggle: (id: string) => void;
  readonly onCreateNoteDraft: (id: string) => void;
}

const AgentPhoneCallRowItem: FC<RowProps> = ({
  row,
  isPlaying,
  isLoading,
  isCreatingNote,
  onPlayToggle,
  onCreateNoteDraft,
}) => {
  const { download, isLoading: isDownloading } = useDownloadCallRecording(row.id, row.clientName, row.date);
  const disabled = !row.isPlayable;

  return (
    <TableRow hover>
      <TableCell>{row.date}</TableCell>
      <TableCell>
        {row.clientId ? (
          <Link
            underline="none"
            component={RouterLink}
            to={`/customers/${row.clientId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.clientName}
          </Link>
        ) : (
          row.clientName
        )}
      </TableCell>
      <TableCell>{row.agentName}</TableCell>
      <TableCell align="right">{row.duration}</TableCell>
      <TableCell align="right">
        <Tooltip title={disabled ? "Brak nagrania" : playButtonLabel(isPlaying)}>
          <span>
            <IconButton
              aria-label={playButtonLabel(isPlaying)}
              disabled={disabled || isLoading}
              size="small"
              onClick={() => onPlayToggle(row.id)}
            >
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={disabled ? "Brak nagrania" : "Pobierz"}>
          <span>
            <IconButton
              aria-label="Pobierz"
              disabled={disabled || isDownloading}
              size="small"
              onClick={() => download()}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={disabled ? "Brak nagrania" : "Stwórz notatkę"}>
          <span>
            <IconButton
              aria-label="Stwórz notatkę"
              disabled={disabled || isCreatingNote}
              size="small"
              onClick={() => onCreateNoteDraft(row.id)}
            >
              <NoteAddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

function playButtonLabel(isPlaying: boolean): string {
  return isPlaying ? "Wstrzymaj" : "Odtwórz";
}

export default AgentCallsList;
